import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  booleanAttribute,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';
import { SearchableDropdownCoordinatorService } from './searchable-dropdown-coordinator.service';

export interface SearchableDropdownOption {
  label: string;
  value: any;
}

@Component({
  selector: 'app-searchable-dropdown',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './searchable-dropdown.component.html',
  styleUrls: ['./searchable-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableDropdownComponent),
      multi: true,
    },
  ],
})
export class SearchableDropdownComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() options: SearchableDropdownOption[] = [];
  @Input() placeholder = 'Select...';
  @Input() label = '';
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input({ transform: booleanAttribute }) showClear = true;
  @Input({ transform: booleanAttribute }) compact = false;
  @Input({ transform: booleanAttribute }) multiSelect = false;
  @Input() selectAllLabel = 'Select All';
  /** Fixed panel width in compact/table mode (px). */
  @Input() compactPanelWidth = 280;

  @Output() selectionChange = new EventEmitter<any>();

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('dropdownPanel') dropdownPanel?: ElementRef<HTMLElement>;

  value: any = null;
  searchText = '';
  isOpen = false;
  isFocused = false;
  isSearching = false;
  filteredOptions: SearchableDropdownOption[] = [];
  panelStyle: Record<string, string> = {};

  private readonly instanceId: string;
  private readonly compactPanelMaxHeight = 200;
  private boundRepositionPanel = () => this.updatePanelPosition();
  private panelMovedToBody = false;
  private coordinatorSub?: Subscription;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.coordinatorSub = this.coordinator.opened$.subscribe((openedId) => {
      if (openedId !== this.instanceId && this.isOpen) {
        this.closeDropdown(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.coordinatorSub?.unsubscribe();
    this.detachPanelListeners();
    if (this.panelMovedToBody) {
      this.dropdownPanel?.nativeElement?.remove();
      this.panelMovedToBody = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] || changes['multiSelect']) {
      this.applyFilter();
      if (!this.isOpen && !this.isSearching) {
        this.searchText = this.getSelectedLabel();
      }
      if (this.isOpen && this.compact) {
        setTimeout(() => {
          this.updatePanelPosition();
          this.cdr.markForCheck();
        });
      }
    }
  }

  writeValue(value: any): void {
    if (this.multiSelect) {
      this.value = Array.isArray(value) ? [...value] : value != null && value !== '' ? [value] : [];
    } else {
      this.value = value ?? null;
    }
    if (!this.isOpen && !this.isSearching) {
      this.searchText = this.getSelectedLabel();
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get hasValue(): boolean {
    if (this.multiSelect) {
      return this.getSelectedValuesArray().length > 0;
    }
    return this.value !== null && this.value !== undefined && this.value !== '';
  }

  get displayText(): string {
    if (this.isOpen || this.isSearching) {
      return this.searchText;
    }
    return this.getSelectedLabel();
  }

  get inputTooltip(): string {
    const label = this.getSelectedLabel();
    return label || this.placeholder;
  }

  /** Options in the current filtered list that participate in Select All. */
  get selectableFilteredOptions(): SearchableDropdownOption[] {
    return this.filteredOptions;
  }

  get isAllFilteredSelected(): boolean {
    const selectable = this.selectableFilteredOptions;
    if (!selectable.length) {
      return false;
    }
    const selected = this.getSelectedValuesArray();
    return selectable.every((opt) => selected.some((v) => this.valuesEqual(v, opt.value)));
  }

  get isIndeterminateFiltered(): boolean {
    const selectable = this.selectableFilteredOptions;
    if (!selectable.length) {
      return false;
    }
    const selected = this.getSelectedValuesArray();
    const selectedCount = selectable.filter((opt) =>
      selected.some((v) => this.valuesEqual(v, opt.value))
    ).length;
    return selectedCount > 0 && selectedCount < selectable.length;
  }

  onControlClick(event: Event): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.openDropdown(true);
  }

  onInputFocus(): void {
    if (this.disabled) {
      return;
    }
    this.isFocused = true;
    this.openDropdown(false);
  }

  onInputBlur(): void {
    setTimeout(() => {
      if (!this.isOpen) {
        this.isFocused = false;
        this.cdr.markForCheck();
      }
    }, 120);
  }

  onSearchInput(text: string): void {
    this.isSearching = true;
    this.searchText = text ?? '';
    if (!this.isOpen) {
      this.openDropdown(false);
      return;
    }
    this.applyFilter();
    if (this.compact) {
      setTimeout(() => this.updatePanelPosition());
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDropdown(true);
      return;
    }
    if (!this.multiSelect && event.key === 'Enter' && this.filteredOptions.length === 1) {
      event.preventDefault();
      this.selectOption(this.filteredOptions[0], event);
    }
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    if (this.isOpen) {
      this.closeDropdown(true);
    } else {
      this.openDropdown(true);
    }
  }

  onClearClick(event: Event): void {
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    if (this.isOpen) {
      this.clearSearchTextOnly();
      return;
    }
    this.clearSelection();
  }

  onSelectAllClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.disabled || !this.multiSelect) {
      return;
    }
    if (this.isAllFilteredSelected) {
      this.unselectAllFiltered();
    } else {
      this.selectAllFiltered();
    }
    this.cdr.markForCheck();
  }

  toggleMultiOption(option: SearchableDropdownOption, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.disabled) {
      return;
    }
    const selected = [...this.getSelectedValuesArray()];
    const index = selected.findIndex((v) => this.valuesEqual(v, option.value));
    if (index >= 0) {
      selected.splice(index, 1);
    } else {
      selected.push(option.value);
    }
    this.setValue(selected);
    this.cdr.markForCheck();
  }

  selectOption(option: SearchableDropdownOption, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.setValue(option.value);
    this.searchText = option.label;
    this.isSearching = false;
    this.closeDropdown(false);
  }

  isSelected(option: SearchableDropdownOption): boolean {
    if (this.multiSelect) {
      return this.getSelectedValuesArray().some((v) => this.valuesEqual(v, option.value));
    }
    return this.valuesEqual(option.value, this.value);
  }

  trackByValue(_index: number, option: SearchableDropdownOption): any {
    return option.value;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }
    const target = event.target as Node;
    const panelEl = this.dropdownPanel?.nativeElement;
    const clickedInside =
      this.elementRef.nativeElement.contains(target) ||
      (panelEl?.contains(target) ?? false);
    if (!clickedInside) {
      this.closeDropdown(true);
    }
  }

  private clearSearchTextOnly(): void {
    this.searchText = '';
    this.isSearching = true;
    this.applyFilter();
    setTimeout(() => this.searchInput?.nativeElement?.focus());
    this.cdr.markForCheck();
  }

  private clearSelection(): void {
    this.setValue(this.multiSelect ? [] : null);
    this.searchText = '';
    this.isSearching = false;
    this.closeDropdown(true);
  }

  private selectAllFiltered(): void {
    const selected = [...this.getSelectedValuesArray()];
    for (const opt of this.selectableFilteredOptions) {
      if (!selected.some((v) => this.valuesEqual(v, opt.value))) {
        selected.push(opt.value);
      }
    }
    this.setValue(selected);
  }

  private unselectAllFiltered(): void {
    const filteredValues = this.selectableFilteredOptions.map((o) => o.value);
    const selected = this.getSelectedValuesArray().filter(
      (v) => !filteredValues.some((fv) => this.valuesEqual(v, fv))
    );
    this.setValue(selected);
  }

  private getSelectedValuesArray(): any[] {
    if (!this.multiSelect) {
      return [];
    }
    return Array.isArray(this.value) ? [...this.value] : [];
  }

  private openDropdown(focusInput: boolean): void {
    this.coordinator.notifyOpened(this.instanceId);
    this.isOpen = true;
    this.isFocused = true;
    this.isSearching = true;
    this.searchText = '';
    this.applyFilter();
    this.cdr.markForCheck();
    if (this.compact) {
      this.attachPanelListeners();
      setTimeout(() => {
        this.mountPanelToBody();
        this.updatePanelPosition();
      });
    }
    if (focusInput) {
      setTimeout(() => this.searchInput?.nativeElement?.focus());
    }
  }

  private closeDropdown(markTouched: boolean): void {
    this.isOpen = false;
    this.isFocused = false;
    this.isSearching = false;
    this.searchText = this.getSelectedLabel();
    this.panelStyle = {};
    this.restorePanelFromBody();
    this.detachPanelListeners();
    if (markTouched) {
      this.onTouched();
    }
    this.cdr.markForCheck();
  }

  private attachPanelListeners(): void {
    this.detachPanelListeners();
    window.addEventListener('scroll', this.boundRepositionPanel, true);
    window.addEventListener('resize', this.boundRepositionPanel);
  }

  private detachPanelListeners(): void {
    window.removeEventListener('scroll', this.boundRepositionPanel, true);
    window.removeEventListener('resize', this.boundRepositionPanel);
  }

  private mountPanelToBody(): void {
    const panel = this.dropdownPanel?.nativeElement;
    if (!panel || panel.parentElement === document.body) {
      return;
    }
    document.body.appendChild(panel);
    this.panelMovedToBody = true;
  }

  private restorePanelFromBody(): void {
    if (!this.panelMovedToBody) {
      return;
    }
    const panel = this.dropdownPanel?.nativeElement;
    if (panel?.parentElement === document.body) {
      this.elementRef.nativeElement.appendChild(panel);
    }
    this.panelMovedToBody = false;
  }

  private updatePanelPosition(): void {
    const control = this.elementRef.nativeElement.querySelector('.dropdown-control') as HTMLElement | null;
    if (!control) {
      return;
    }

    const rect = control.getBoundingClientRect();
    const viewportPadding = 8;
    const panelWidth = Math.min(
      Math.max(this.compactPanelWidth, rect.width),
      window.innerWidth - viewportPadding * 2
    );

    let left = rect.left;
    if (left + panelWidth > window.innerWidth - viewportPadding) {
      left = Math.max(viewportPadding, rect.right - panelWidth);
    }
    left = Math.max(viewportPadding, left);

    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    let top = rect.bottom + gap;
    let maxHeight = this.compactPanelMaxHeight;

    if (spaceBelow < 120 && spaceAbove > spaceBelow) {
      maxHeight = Math.min(this.compactPanelMaxHeight, spaceAbove - gap);
      top = Math.max(viewportPadding, rect.top - maxHeight - gap);
    } else {
      maxHeight = Math.min(this.compactPanelMaxHeight, spaceBelow - gap);
    }

    this.panelStyle = {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      width: `${panelWidth}px`,
      maxHeight: `${Math.max(80, maxHeight)}px`,
      zIndex: '10000',
      backgroundColor: '#ffffff',
      border: '1px solid #c5cad3',
      borderRadius: '6px',
      boxShadow: '0 10px 24px rgba(15, 23, 42, 0.16)',
      boxSizing: 'border-box',
    };
  }

  private setValue(value: any): void {
    if (this.multiSelect) {
      this.value = Array.isArray(value) ? [...value] : [];
    } else {
      this.value = value;
    }
    this.onChange(this.multiSelect ? [...this.getSelectedValuesArray()] : this.value);
    this.selectionChange.emit(this.multiSelect ? [...this.getSelectedValuesArray()] : this.value);
  }

  private applyFilter(): void {
    const query = String(this.searchText ?? '').trim().toLowerCase();
    const opts = Array.isArray(this.options) ? this.options : [];
    this.filteredOptions = !query
      ? [...opts]
      : opts.filter((option) => String(option.label ?? '').toLowerCase().includes(query));
  }

  private getSelectedLabel(): string {
    if (this.multiSelect) {
      const selected = this.getSelectedValuesArray();
      if (!selected.length) {
        return '';
      }
      const labels = selected
        .map((v) => {
          const match = this.options.find((o) => this.valuesEqual(o.value, v));
          return match ? match.label : String(v);
        })
        .filter((l) => l);
      if (!labels.length) {
        return '';
      }
      if (labels.length <= 2) {
        return labels.join(', ');
      }
      return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`;
    }

    if (!this.hasValue) {
      return '';
    }
    const match = this.options.find((option) => this.valuesEqual(option.value, this.value));
    return match ? match.label : String(this.value);
  }

  private valuesEqual(a: any, b: any): boolean {
    if (a == null && b == null) {
      return true;
    }
    if (a == null || b == null) {
      return false;
    }
    return String(a) === String(b);
  }

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    private coordinator: SearchableDropdownCoordinatorService
  ) {
    this.instanceId = this.coordinator.createInstanceId();
  }
}
